import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import { getApiEndpoints } from '@/services/api/endpoints';
import type {
  Subscription,
  SubscriptionStatus,
  SubscriptionTier,
  UpdateSubscriptionRequest,
  UpdateSubscriptionResponse,
  AssignSubscriptionRequest,
  AssignSubscriptionResponse,
} from './subscriptionTypes';

// Fetch all subscriptions with optional status and tier filters - Super Admin only
export const fetchSubscriptions = createAppAsyncThunk<
  Subscription[],
  { status?: SubscriptionStatus; tier?: SubscriptionTier } | void
>('subscriptions/fetchSubscriptions', async (params, { extra: { config }, rejectWithValue }) => {
  const { apiService, logger } = config;

  try {
    let endpoint = getApiEndpoints().core.adminSubscriptions.getSubscriptions;
    const queryParams: string[] = [];

    if (params?.status) {
      queryParams.push(`status=${params.status}`);
    }
    if (params?.tier) {
      queryParams.push(`tier=${params.tier}`);
    }

    if (queryParams.length > 0) {
      endpoint = `${endpoint}?${queryParams.join('&')}`;
    }
    logger?.info('[fetchSubscriptions] Fetching subscriptions', { endpoint, params });

    const response = await apiService.get<{ items: Subscription[]; nextToken: string | null; total: number }>(endpoint);
    logger?.debug('[fetchSubscriptions] Response received', {
      total: response.total,
      items: response.items.length,
      hasNextToken: Boolean(response.nextToken),
    });

    return response.items;
  } catch (err) {
    const error = err as Error & { status?: number; body?: string };
    logger?.error('[fetchSubscriptions] Failed to fetch subscriptions', {
      message: error.message,
      status: error.status,
      body: error.body,
    });

    let errorMessage = error.message;
    if (error.body) {
      try {
        const body = JSON.parse(error.body);
        errorMessage = body.message || error.message;
      } catch {
        // Keep original message
      }
    }

    return rejectWithValue({ message: errorMessage });
  }
});

// Update a subscription - Super Admin only
export const updateSubscription = createAppAsyncThunk<
  UpdateSubscriptionResponse,
  { id: string; data: UpdateSubscriptionRequest }
>('subscriptions/updateSubscription', async ({ id, data }, { extra: { config }, rejectWithValue }) => {
  const { apiService, logger } = config;

  try {
    const endpoint = `${getApiEndpoints().core.adminSubscriptions.updateSubscription}/${id}`;
    logger?.info('[updateSubscription] Updating subscription', { id, data });

    const response = await apiService.put<UpdateSubscriptionResponse>(endpoint, data);
    logger?.debug('[updateSubscription] Response received', { id });

    // API returns subscription directly
    return response;
  } catch (err) {
    const error = err as Error & { status?: number; body?: string };
    logger?.error('[updateSubscription] Failed to update subscription', {
      id,
      message: error.message,
      status: error.status,
      body: error.body,
    });

    let errorMessage = error.message;
    if (error.body) {
      try {
        const body = JSON.parse(error.body);
        errorMessage = body.message || error.message;
      } catch {
        // Keep original message
      }
    }

    return rejectWithValue({ message: errorMessage });
  }
});

// Assign subscription to a user - Super Admin only
export const assignSubscription = createAppAsyncThunk<AssignSubscriptionResponse, AssignSubscriptionRequest>(
  'subscriptions/assignSubscription',
  async (data, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.adminSubscriptions.assignSubscription;
      logger?.info('[assignSubscription] Assigning subscription', data);

      const response = await apiService.post<AssignSubscriptionResponse>(endpoint, data);
      logger?.debug('[assignSubscription] Response received', { success: response.success });

      if (!response.success) {
        return rejectWithValue({ message: response.message || 'Failed to assign subscription' });
      }

      return response;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[assignSubscription] Failed to assign subscription', {
        message: error.message,
        status: error.status,
        body: error.body,
      });

      let errorMessage = error.message;
      if (error.body) {
        try {
          const body = JSON.parse(error.body);
          errorMessage = body.message || error.message;
        } catch {
          // Keep original message
        }
      }

      return rejectWithValue({ message: errorMessage });
    }
  },
);
